import { Col, Row } from "react-bootstrap";
import { formatCurrency } from "./SCRV2InvoiceDetailHelpers";
import { getScrV2CollectionEfficiencyColor, SCR_V2_NESTED_CARD_STYLE } from "./SCRV2SummaryUiShared";

export const ScrV2SummaryAmountPlaceholder = () => (
  <span
    className="d-inline-block rounded"
    style={{
      width: 88,
      height: 18,
      background: "linear-gradient(90deg, #e9ecef 25%, #f8f9fa 50%, #e9ecef 75%)",
      backgroundSize: "200% 100%",
      animation: "scrSummaryShimmer 1.2s ease-in-out infinite",
    }}
  />
);

export const formatScrV2SummaryCurrencyOrPlaceholder = (value, showPlaceholder) =>
  showPlaceholder ? <ScrV2SummaryAmountPlaceholder /> : formatCurrency(value);

export const ScrV2CollectionEfficiencyRing = ({ rate, showPlaceholder = false }) => {
  const pct = Math.min(Math.max(Number(rate || 0), 0), 100);
  const ringColor = showPlaceholder ? "#e9ecef" : getScrV2CollectionEfficiencyColor(pct);

  return (
    <div className="text-center py-2 scr-summary-efficiency-ring">
      <div
        style={{
          width: 148,
          height: 148,
          borderRadius: "50%",
          background: showPlaceholder
            ? "#e9ecef"
            : `conic-gradient(${ringColor} ${pct * 3.6}deg, #e9ecef ${pct * 3.6}deg)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            width: 118,
            height: 118,
            borderRadius: "50%",
            background: "#fff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {showPlaceholder ? (
            <ScrV2SummaryAmountPlaceholder />
          ) : (
            <>
              <div className="font-weight-bold text-dark" style={{ fontSize: "1.65rem", lineHeight: 1 }}>
                {pct}%
              </div>
              <div
                className="text-muted text-uppercase mt-1"
                style={{ fontSize: "0.78rem", letterSpacing: "0.06em" }}
              >
                Efficiency
              </div>
            </>
          )}
        </div>
      </div>
      {!showPlaceholder ? (
        <div
          className="d-flex justify-content-center mt-3 text-muted"
          style={{ fontSize: "0.85rem" }}
        >
          <span className="mr-3">
            <span style={{ color: ringColor }}>●</span> Received
          </span>
          <span>
            <span style={{ color: "#ced4da" }}>●</span> Pending
          </span>
        </div>
      ) : null}
    </div>
  );
};

export const formatScrV2TransactionCount = (count) =>
  count === null || count === undefined ? "—" : count;

export const sumScrV2TransactionCounts = (items) =>
  (items || []).reduce((total, item) => total + Number(item.transactionCount || 0), 0);

export const ScrV2ExpandablePaymentGroup = ({ title, amount, count, open, onToggle, children }) => (
  <>
    <tr
      className="bg-light scr-summary-payment-group"
      style={{ cursor: "pointer" }}
      onClick={onToggle}
    >
      <td className="pl-3 font-weight-bold text-dark">
        <i
          className={`fas fa-chevron-${open ? "down" : "right"} mr-2 text-muted scr-summary-payment-chevron`}
          style={{ width: 12 }}
        />
        {title}
      </td>
      <td className="text-center font-weight-bold">{formatScrV2TransactionCount(count)}</td>
      <td className="text-right pr-3 font-weight-bold">{formatCurrency(amount)}</td>
    </tr>
    {open ? children : null}
  </>
);

export const ScrV2PaymentTypeRow = ({ label, count, amount, indent = false }) => (
  <tr>
    <td className={indent ? "pl-5" : "pl-4"}>{label}</td>
    <td className="text-center">{formatScrV2TransactionCount(count)}</td>
    <td className="text-right pr-3 font-weight-bold">{formatCurrency(amount)}</td>
  </tr>
);

export const ScrV2SummaryAmountRow = ({
  label,
  value,
  deduct,
  signed,
  strong,
  highlight,
  showPlaceholder = false,
}) => {
  const amount = Number(value || 0);

  const renderValue = () => {
    if (showPlaceholder) {
      return <ScrV2SummaryAmountPlaceholder />;
    }

    if (signed) {
      if (amount < 0) {
        return <span className="text-danger font-weight-bold">- {formatCurrency(amount)}</span>;
      }
      if (amount > 0) {
        return <span className="text-success font-weight-bold">+ {formatCurrency(amount)}</span>;
      }
      return <b>{formatCurrency(0)}</b>;
    }

    if (deduct) {
      return <span className="text-danger font-weight-bold">- {formatCurrency(amount)}</span>;
    }

    return <b className={highlight ? "text-primary" : undefined}>{formatCurrency(amount)}</b>;
  };

  return (
    <Row className={strong ? "border-top pt-2 mt-1 mx-0" : "mx-0 mb-1"}>
      <Col xs={8}>
        <label className={`col-form-label py-0 mb-0 ${strong ? "font-weight-bold" : ""}`}>
          {label}
        </label>
      </Col>
      <Col xs={4} className="text-right">
        {renderValue()}
      </Col>
    </Row>
  );
};

export const ScrV2SummaryPanel = ({
  title,
  badge,
  icon,
  iconBg = "bg-primary",
  children,
  bodyClassName = "py-2",
  className = "",
}) => (
  <div className={`card border scr-summary-panel ${className}`.trim()} style={SCR_V2_NESTED_CARD_STYLE}>
    <div className="card-header py-2 px-2 d-flex align-items-center bg-white border-0">
      <h6 className="card-title mb-0 d-flex align-items-center flex-grow-1 min-w-0 mr-2">
        {icon ? (
          <span
            className={`d-inline-flex align-items-center justify-content-center rounded ${iconBg} text-white mr-1 flex-shrink-0`}
            style={{ width: 22, height: 22, fontSize: "0.7rem" }}
          >
            <i className={`fas ${icon}`} />
          </span>
        ) : null}
        <b>{title}</b>
      </h6>
      {badge ? (
        <span className="badge badge-light border text-dark ml-auto flex-shrink-0">
          {badge}
        </span>
      ) : null}
    </div>
    <div className={`card-body px-2 ${bodyClassName}`}>{children}</div>
  </div>
);
